import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";

import api from "@/pages/services/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import CourseStoreHeader from "./coursestore/CourseStoreHeader";
import StorefrontHero from "./coursestore/StorefrontHero";
import CourseGrid from "./coursestore/CourseGrid";
import CourseDetails from "./coursestore/CourseDetails";
import PricingCard from "./coursestore/PricingCard";

const formatInr = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const calculateSavings = (original, discount) => {
  const orig = Number(String(original || "0").replace(/,/g, ""));
  const disc = Number(String(discount || "0").replace(/,/g, ""));
  return formatInr(orig - disc);
};

const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function AllCourses() {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const navigate = useNavigate();

  // Fetch courses from API on mount
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        // Public catalog — courses an admin has flagged for the storefront.
        const { data } = await api.get(
          "/courses?listInStore=true&isArchived=false&limit=100"
        );

        const allCourses = (data?.courses || []).map((course) => ({
          ...course,
          id: Number(course.id),
          fee: Number(course.fee || 0),
          type: course.isGroupCourse ? "group" : "recorded",
          // Format price for display
          originalPrice: course.fee ? formatInr(course.fee) : formatInr(0),
          discountPrice: course.fee ? formatInr(course.fee) : formatInr(0),
          image: `/logo-square.png`,
          description:
            course.description ||
            "Build strong foundations in Data Analysis, Business Intelligence, and AI powered analytics tools used by modern organizations.",
          duration: "4 to 6 Months",
          modulesTitle: "Technologies Covered",
          modules: [
            "Advanced Technologies",
            "Industry Best Practices",
            "Hands-on Projects",
            "Real-world Case Studies",
            "Expert Mentorship",
            "Professional Certification",
          ],
          benefits: [
            "1 Year Training and Placement Support",
            "Expert Mentorship from Industry Professionals",
            "15 Mock Interview Sessions",
            "Resume, Certification and Profile Building",
            "1 Repeat Batch Option for revision and practice",
          ],
          savings: calculateSavings(course.fee, course.fee),
        }));

        setCourses(allCourses);
      } catch (error) {
        toast.error(error.response?.data?.msg || "Failed to load courses");
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const startPurchaseFlow = (course) => {
    const token = localStorage.getItem("token");
    const user = getCurrentUser();
    const learnerCheckoutPath = `/learner/courses/all?search=${encodeURIComponent(
      course.title
    )}`;

    if (token && user?.role === "learner") {
      navigate(learnerCheckoutPath);
      return;
    }

    if (token && user?.role && user.role !== "learner") {
      navigate(`/${user.role}`);
      return;
    }

    navigate("/login", {
      state: {
        postLoginRedirect: learnerCheckoutPath,
        source: "coursestore",
      },
    });
  };

  // --- STOREFRONT VIEW (Grid of all courses) ---
  if (!selectedCourse) {
    return (
      <div className="min-h-screen bg-linear-to-br from-background via-card to-muted/30 font-sans text-foreground pb-20">
        <CourseStoreHeader
          onBack={() => navigate("/")}
          selectedCourse={selectedCourse}
        />

        <StorefrontHero />

        {/* "Our Courses" Section Header */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 mb-6">
          <h2 className="text-2xl font-bold text-foreground tracking-tight">
            Our Courses
          </h2>
        </div>

        {/* Course Grid */}
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <CourseGrid
            courses={courses}
            onCourseSelect={setSelectedCourse}
            loading={loading}
          />
        </div>
      </div>
    );
  }

  // --- COURSE DETAILS VIEW ---
  return (
    <div className="min-h-screen bg-linear-to-b from-muted/20 to-background font-sans text-foreground pb-24">
      <CourseStoreHeader
        onBack={() => setSelectedCourse(null)}
        selectedCourse={selectedCourse}
      />

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-4">
        <Button
          onClick={() => setSelectedCourse(null)}
          variant="ghost"
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all courses
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* LEFT COLUMN: Content */}
        <div className="lg:col-span-8">
          {/* Course Image */}
          <Card className="w-full rounded-2xl overflow-hidden border-border shadow-sm mb-10">
            <img
              src={selectedCourse.image}
              alt={selectedCourse.title}
              className="w-full h-auto object-cover"
              onError={(e) => {
                e.currentTarget.src = "/logo-square.png";
              }}
            />
          </Card>

          {/* Course Details */}
          <CourseDetails course={selectedCourse} />
        </div>

        {/* RIGHT COLUMN: Pricing Card */}
        <div className="lg:col-span-4">
          <PricingCard
            course={selectedCourse}
            onCheckout={() => startPurchaseFlow(selectedCourse)}
            loading={isCheckingOut}
          />
        </div>
      </div>
    </div>
  );
}
